import React from 'react';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { Box } from '@mui/material';
import CustomDivider from '../Divider/CustomDivider';

export interface ToggleButtonOption {
  value: string | number;
  label?: string;
  icon?: React.ReactNode;
}

export interface ToggleButtonProps {
  id: string;
  options: ToggleButtonOption[];
  value: string | string[];
  onChange?: (value: string | string[]) => void;
  onSelectChange?: (value: string | string[]) => void; // 🔹 New prop for state updates
  multiple?: boolean;
  exclusive?: boolean;
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  fullWidth?: boolean;
}

const CustomToggleButton: React.FC<ToggleButtonProps> = ({
  id,
  options,
  value,
  onChange,
  onSelectChange, // 🔹 Accept external state update function
  multiple = false,
  exclusive = true,
  size = 'medium',
  disabled = false,
  fullWidth = false,
}) => {
  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: string | string[] | null
  ) => {
    if (!newValue) return; // Ensure valid selection

    if (!multiple && exclusive) {
      onChange?.(newValue);
      onSelectChange?.(newValue);
    } else {
      onChange?.(newValue ?? []);
      onSelectChange?.(newValue ?? []);
    }
  };

  return (
    <ToggleButtonGroup
      id={id}
      value={value}
      onChange={handleChange}
      exclusive={exclusive}
      size={size}
      disabled={disabled}
      fullWidth={fullWidth}
    >
      {options.map(({ value: optionValue, label, icon }, index) => {
        const isSelected = Array.isArray(value)
          ? value.includes(optionValue.toString())
          : value === optionValue;

        return (
          <Box key={optionValue} sx={{ display: 'flex', alignItems: 'center' }}>
            <ToggleButton
              value={optionValue.toString()}
              disableRipple
              sx={{
                position: 'relative',
                '&.Mui-selected': {
                  color: '#03A3DB',
                },
                padding: '0px 12px',
              }}
            >
              {icon}
              {label}
              {isSelected && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '-4px',
                    left: '25%',
                    right: '25%',
                    width: '50%',
                    height: '2px',
                    backgroundColor: '#03A3DB',
                  }}
                />
              )}
            </ToggleButton>
            {index < options.length - 1 && (
              <CustomDivider orientation="vertical" flexItem />
            )}
          </Box>
        );
      })}
    </ToggleButtonGroup>
  );
};

export default CustomToggleButton;
